import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  Download,
  RotateCcw,
  Columns,
  Eye,
  Rocket,
} from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import {
  useImportProducts,
  parseCSVHeaders,
  PRODUCT_FIELDS,
} from '../../services/bulkService';

const STEPS = ['upload', 'map', 'preview', 'done'];

/**
 * Admin CSV Import page — multi-step wizard:
 * 1. Upload CSV file
 * 2. Map CSV columns to product fields
 * 3. Dry-run preview with error report
 * 4. Confirm & import
 */
export default function AdminImportPage() {
  const [step, setStep] = useState('upload');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [csvInfo, setCsvInfo] = useState(null); // { headers, previewRows, totalRows }
  const [columnMap, setColumnMap] = useState({}); // { csvHeader: dbField }
  const [dryRunResult, setDryRunResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const importMutation = useImportProducts();

  // ── File handling ──
  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      alert('Please upload a .csv file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvText(text);
      const info = parseCSVHeaders(text);
      setCsvInfo(info);

      // Auto-map columns by name matching
      const autoMap = {};
      info.headers.forEach((h) => {
        const lower = h.toLowerCase().replace(/[^a-z]/g, '');
        const match = PRODUCT_FIELDS.find(
          (f) => f.key === lower || f.label.toLowerCase().replace(/[^a-z]/g, '') === lower
        );
        if (match) autoMap[h] = match.key;
      });
      setColumnMap(autoMap);
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      handleFile(file);
    },
    [handleFile]
  );

  // ── Dry run ──
  const runDryRun = () => {
    importMutation.mutate(
      { csvText, columnMap, dryRun: true },
      {
        onSuccess: (data) => {
          setDryRunResult(data);
          setStep('preview');
        },
      }
    );
  };

  // ── Actual import ──
  const runImport = () => {
    importMutation.mutate(
      { csvText, columnMap, dryRun: false },
      {
        onSuccess: (data) => {
          setImportResult(data);
          setStep('done');
        },
      }
    );
  };

  // ── Reset ──
  const reset = () => {
    setStep('upload');
    setCsvText('');
    setFileName('');
    setCsvInfo(null);
    setColumnMap({});
    setDryRunResult(null);
    setImportResult(null);
    importMutation.reset();
  };

  // ── Template download ──
  const downloadTemplate = () => {
    const headers = PRODUCT_FIELDS.map((f) => f.label).join(',');
    const example = ['Smash Burger', '850', 'smash-burger', 'Burgers', 'Juicy beef patty', 'Our signature double smash burger with cheddar cheese', '50', 'true', ''].join(',');
    const csv = [headers, example].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const mappedRequired = PRODUCT_FIELDS.filter((f) => f.required);
  const allRequiredMapped = mappedRequired.every((f) =>
    Object.values(columnMap).includes(f.key)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Import Products</h1>
          <p className="text-sm text-brand-gray mt-1">
            Upload a CSV file to bulk-import products
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
          >
            <Download size={16} />
            Template
          </button>
          <Link
            to="/admin/products"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
          >
            <ChevronLeft size={16} />
            Back to Products
          </Link>
        </div>
      </div>

      {/* Progress steps */}
      <div className="flex items-center gap-2 px-1">
        {STEPS.map((s, i) => {
          const stepIndex = STEPS.indexOf(step);
          const thisIndex = i;
          const isActive = s === step;
          const isDone = thisIndex < stepIndex;
          return (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors',
                  isActive
                    ? 'bg-brand-red text-brand-white'
                    : isDone
                      ? 'bg-success text-brand-white'
                      : 'bg-brand-light text-brand-gray'
                )}
              >
                {isDone ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span
                className={clsx(
                  'text-xs font-bold capitalize hidden sm:inline',
                  isActive ? 'text-brand-dark' : 'text-brand-gray'
                )}
              >
                {s === 'done' ? 'Complete' : s}
              </span>
              {i < STEPS.length - 1 && (
                <div
                  className={clsx(
                    'flex-1 h-0.5 rounded-full',
                    isDone ? 'bg-success' : 'bg-ui-border'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── STEP 1: Upload ── */}
      <AnimatePresence mode="wait">
        {step === 'upload' && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={clsx(
                'border-2 border-dashed rounded-2xl p-12 text-center transition-colors',
                dragOver
                  ? 'border-brand-red bg-brand-red/5'
                  : 'border-ui-border bg-brand-white hover:border-brand-gray'
              )}
            >
              <Upload size={48} className="mx-auto text-ui-muted mb-4" />
              <h3 className="text-lg font-bold text-brand-dark mb-1">
                Drop your CSV file here
              </h3>
              <p className="text-sm text-brand-gray mb-4">
                or click to browse files
              </p>
              <label className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red hover:bg-red-700 text-brand-white font-bold text-sm rounded-xl transition-colors cursor-pointer">
                <FileSpreadsheet size={16} />
                Choose CSV File
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-ui-muted mt-4">
                Maximum 500 rows • Must include Title and Price columns
              </p>
            </div>
          </motion.div>
        )}

        {/* ── STEP 2: Column Mapping ── */}
        {step === 'map' && csvInfo && (
          <motion.div
            key="map"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-5"
          >
            {/* File info */}
            <div className="flex items-center gap-3 p-4 bg-brand-white border border-ui-border rounded-xl">
              <FileSpreadsheet size={24} className="text-success flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-brand-dark truncate">{fileName}</p>
                <p className="text-xs text-brand-gray">
                  {csvInfo.totalRows} row{csvInfo.totalRows !== 1 ? 's' : ''} • {csvInfo.headers.length} columns
                </p>
              </div>
              <button
                onClick={reset}
                className="p-2 rounded-lg text-brand-gray hover:bg-brand-light transition-colors"
              >
                <RotateCcw size={16} />
              </button>
            </div>

            {/* Column mapping */}
            <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-ui-background border-b border-ui-border">
                <div className="flex items-center gap-2">
                  <Columns size={16} className="text-brand-red" />
                  <h3 className="text-sm font-bold text-brand-dark">Map CSV Columns</h3>
                </div>
                <p className="text-xs text-brand-gray mt-1">
                  Match each CSV column to a product field. <span className="text-error">*</span> = required
                </p>
              </div>
              <div className="divide-y divide-ui-border">
                {csvInfo.headers.map((header) => (
                  <div
                    key={header}
                    className="flex items-center gap-4 px-5 py-3"
                  >
                    <span className="text-sm font-medium text-brand-dark w-40 truncate flex-shrink-0">
                      {header}
                    </span>
                    <ArrowRight size={14} className="text-ui-muted flex-shrink-0" />
                    <select
                      value={columnMap[header] || ''}
                      onChange={(e) => {
                        setColumnMap((prev) => {
                          const next = { ...prev };
                          if (e.target.value) next[header] = e.target.value;
                          else delete next[header];
                          return next;
                        });
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    >
                      <option value="">— Skip —</option>
                      {PRODUCT_FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label} {f.required ? '*' : ''}
                        </option>
                      ))}
                    </select>
                    {columnMap[header] && (
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Preview of first rows */}
            {csvInfo.previewRows.length > 0 && (
              <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
                <div className="px-5 py-3 bg-ui-background border-b border-ui-border flex items-center gap-2">
                  <Eye size={16} className="text-brand-red" />
                  <h3 className="text-sm font-bold text-brand-dark">Data Preview</h3>
                  <span className="text-xs text-brand-gray">(first 5 rows)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-ui-border bg-brand-light/50">
                        <th className="px-4 py-2 text-left text-xs font-bold text-brand-gray">#</th>
                        {csvInfo.headers
                          .filter((h) => columnMap[h])
                          .map((h) => (
                            <th key={h} className="px-4 py-2 text-left text-xs font-bold text-brand-gray">
                              {PRODUCT_FIELDS.find((f) => f.key === columnMap[h])?.label || h}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ui-border">
                      {csvInfo.previewRows.map((row, i) => (
                        <tr key={i} className="hover:bg-brand-light/30">
                          <td className="px-4 py-2 text-xs text-brand-gray">{i + 1}</td>
                          {csvInfo.headers
                            .filter((h) => columnMap[h])
                            .map((h) => (
                              <td key={h} className="px-4 py-2 text-xs text-brand-dark max-w-[200px] truncate">
                                {row[h] || '—'}
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
              >
                <ChevronLeft size={16} />
                Back
              </button>
              <button
                onClick={runDryRun}
                disabled={!allRequiredMapped || importMutation.isPending}
                className={clsx(
                  'inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-colors',
                  allRequiredMapped
                    ? 'bg-brand-red hover:bg-red-700 text-brand-white'
                    : 'bg-brand-light text-brand-gray cursor-not-allowed'
                )}
              >
                {importMutation.isPending ? (
                  <><Loader2 size={16} className="animate-spin" /> Validating…</>
                ) : (
                  <><Eye size={16} /> Validate & Preview</>
                )}
              </button>
            </div>

            {/* Mutation error */}
            {importMutation.isError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-error font-medium">
                <AlertTriangle size={16} />
                {importMutation.error?.message || 'Validation failed'}
              </div>
            )}
          </motion.div>
        )}

        {/* ── STEP 3: Dry-run preview ── */}
        {step === 'preview' && dryRunResult && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-5"
          >
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-brand-white border border-ui-border rounded-xl p-4 text-center">
                <p className="text-2xl font-extrabold text-brand-dark">{dryRunResult.total}</p>
                <p className="text-xs font-bold text-brand-gray mt-1">Total Rows</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-extrabold text-success">{dryRunResult.valid}</p>
                <p className="text-xs font-bold text-success mt-1">Valid</p>
              </div>
              <div className={clsx(
                'rounded-xl p-4 text-center border',
                dryRunResult.errors > 0
                  ? 'bg-red-50 border-red-200'
                  : 'bg-brand-white border-ui-border'
              )}>
                <p className={clsx('text-2xl font-extrabold', dryRunResult.errors > 0 ? 'text-error' : 'text-brand-dark')}>
                  {dryRunResult.errors}
                </p>
                <p className={clsx('text-xs font-bold mt-1', dryRunResult.errors > 0 ? 'text-error' : 'text-brand-gray')}>
                  Errors
                </p>
              </div>
            </div>

            {/* Row-by-row results */}
            <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-ui-background border-b border-ui-border flex items-center gap-2">
                <FileSpreadsheet size={16} className="text-brand-red" />
                <h3 className="text-sm font-bold text-brand-dark">Row-by-Row Report</h3>
              </div>
              <div className="max-h-[400px] overflow-y-auto divide-y divide-ui-border">
                {dryRunResult.results.map((r) => (
                  <div
                    key={r.row}
                    className={clsx(
                      'flex items-start gap-3 px-5 py-3',
                      r.status === 'error' ? 'bg-red-50/50' : 'hover:bg-brand-light/30'
                    )}
                  >
                    <span className="text-xs font-bold text-brand-gray w-8 flex-shrink-0 pt-0.5">
                      #{r.row}
                    </span>
                    {r.status === 'valid' ? (
                      <CheckCircle2 size={16} className="text-success flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle size={16} className="text-error flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      {r.status === 'valid' && r.data && (
                        <p className="text-sm text-brand-dark truncate">
                          {r.data.title} — {r.data.price ? `KES ${r.data.price}` : ''}
                        </p>
                      )}
                      {r.status === 'error' &&
                        r.errors.map((err, ei) => (
                          <p key={ei} className="text-sm text-error">
                            {err}
                          </p>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep('map')}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
              >
                <ChevronLeft size={16} />
                Fix Mapping
              </button>
              <button
                onClick={runImport}
                disabled={dryRunResult.valid === 0 || importMutation.isPending}
                className={clsx(
                  'inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-colors',
                  dryRunResult.valid > 0
                    ? 'bg-success hover:bg-green-700 text-brand-white'
                    : 'bg-brand-light text-brand-gray cursor-not-allowed'
                )}
              >
                {importMutation.isPending ? (
                  <><Loader2 size={16} className="animate-spin" /> Importing…</>
                ) : (
                  <><Rocket size={16} /> Import {dryRunResult.valid} Products</>
                )}
              </button>
            </div>

            {importMutation.isError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-error font-medium">
                <AlertTriangle size={16} />
                {importMutation.error?.message || 'Import failed'}
              </div>
            )}
          </motion.div>
        )}

        {/* ── STEP 4: Done ── */}
        {step === 'done' && importResult && (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="bg-brand-white border border-ui-border rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h2 className="text-xl font-extrabold text-brand-dark mb-2">Import Complete!</h2>
              <p className="text-sm text-brand-gray mb-6">
                {importResult.message}
              </p>

              <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto mb-6">
                <div className="p-3 bg-brand-light rounded-xl">
                  <p className="text-lg font-extrabold text-brand-dark">{importResult.total}</p>
                  <p className="text-xs text-brand-gray">Total</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl">
                  <p className="text-lg font-extrabold text-success">{importResult.inserted || 0}</p>
                  <p className="text-xs text-success">Imported</p>
                </div>
                <div className="p-3 bg-red-50 rounded-xl">
                  <p className="text-lg font-extrabold text-error">
                    {(importResult.errors || 0) + (importResult.insert_errors || 0)}
                  </p>
                  <p className="text-xs text-error">Failed</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                <Link
                  to="/admin/products"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors"
                >
                  View Products
                </Link>
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
                >
                  <RotateCcw size={16} />
                  Import More
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
